import { parts, type Part } from './part'
import { mechanisms } from './mechanism'
import { AXIS_KEYS, type AxisKey, type Rig, type RigNode } from './rig'
import type { HumanoidModel, Skeleton } from '@/domain/hs1'

/**
 * URDF — der Achsbaum in dem Format, das alle anderen lesen.
 *
 * WOFÜR. Bis hierher lebt das Modell nur in dieser Anwendung. Wer damit in
 * Gazebo, MuJoCo, ROS oder einem Regelungsentwurf weiterarbeiten will, tippt es
 * heute ab — und ab dem Tag gibt es zwei Modelle, die auseinanderlaufen.
 * URDF ist der kleinste gemeinsame Nenner: Glieder, Gelenke, Achsen, Grenzen,
 * Massen. Genau das, was dieser Kern ohnehin führt.
 *
 * ES WIRD ERZEUGT UND NICHT GEPFLEGT. Kein Handgriff, keine Vorlage mit
 * Platzhaltern — wer einen Punkt verschiebt, bekommt ein anderes URDF. Eine
 * gepflegte Datei daneben wäre die zweite Wahrheit, die dieses Projekt an jeder
 * Stelle vermeidet.
 *
 * WAS URDF NICHT KANN, und was deshalb hier NICHT hineingeschrieben wird:
 *
 *   – Geschlossene Ketten. Die Kurbelschwinge am Knie und die
 *     Parallelkinematik am Rumpf sind Schleifen; URDF kennt nur Bäume. Sie
 *     stehen als EIN Drehgelenk darin, mit der Übersetzung als Kommentar.
 *     Ein erfundenes Ersatzgelenk wäre schlimmer als der ehrliche Hinweis.
 *   – Getriebe und Wirkungsgrade. `<transmission>` ist in URDF ein Anhängsel
 *     ohne feste Auslegung; die Zahlen stehen als Kommentar an der Achse.
 *   – Der Zehenfeder ihre Kennlinie. Ein passives Gelenk steht als
 *     `continuous` mit Anschlägen; die Feder ist in URDF nicht ausdrückbar.
 *
 * MASSEN: die Trägheiten sind aus Zylindern gerechnet, nicht gemessen. Das
 * steht so in der Datei. Wer sie für eine Regelungsauslegung nimmt, soll sehen,
 * woher sie kommen.
 */

export interface UrdfOptions {
  /** Name des Roboters in der Datei. */
  name?: string
  /** Stillgelegte Achsen als festes Gelenk statt als Drehgelenk. */
  off?: ReadonlySet<string>
  /** Die Modellkennung für den Dateikopf — siehe `handoff/fingerprint.ts`. */
  modelId?: string
}

/**
 * Schreibt den Achsbaum als URDF.
 *
 * Reine Funktion: gleiche Eingaben, gleiche Datei, Zeichen für Zeichen. Damit
 * lässt sich der Export prüfen wie jede andere Rechnung.
 */
export function toUrdf(
  model: HumanoidModel,
  skeleton: Skeleton,
  rig: Rig,
  options: UrdfOptions = {},
): string {
  const name = options.name ?? 'gen5'
  const off = options.off ?? new Set<string>()
  const mechs = new Map(mechanisms(rig).map((m) => [m.node, m]))
  const byCarrier = groupParts(parts(model, skeleton))

  const lines: string[] = []
  lines.push('<?xml version="1.0"?>')
  lines.push('<!--')
  lines.push('  Erzeugt aus dem Achsbaum des NEURA-Performance-Simulators.')
  lines.push('  NICHT VON HAND ÄNDERN — die Quelle ist der Punktesatz, nicht diese Datei.')
  lines.push('')
  if (options.modelId) {
    lines.push(`  Modellstand: ${escape(options.modelId)}`)
    lines.push('')
  }
  lines.push('  Geschlossene Ketten (Knie, Rumpf) stehen als EIN Drehgelenk. URDF kennt')
  lines.push('  keine Schleifen; die Übersetzung steht als Kommentar an der Achse.')
  lines.push('  Traegheiten sind aus Zylindern gerechnet und nicht gemessen.')
  lines.push('-->')
  lines.push(`<robot name="${escape(name)}">`)

  /*
    DIE WURZEL TRÄGT SELBST FREIHEITEN, und die gingen im ersten Entwurf
    verloren.

    Das Kreuzgelenk des Rumpfaktuators ist der Ursprung des Modells UND ein
    Gelenk mit zwei Achsen. Wer nur Knoten mit Elternteil ausgibt, exportiert
    einen Roboter mit steifem Rumpf — und sieht es nicht, weil die Datei
    fehlerfrei bleibt. Deshalb bekommt die Wurzel ein Grundglied über sich,
    an dem ihre Achsen hängen. `base_link` ist dafür der übliche Name.
  */
  const root = rig.byId[rig.order[0]]
  const rootFree = AXIS_KEYS.some((a) => root.freedom[a] !== null)
  if (rootFree) lines.push('  <link name="base_link"/>')

  for (const id of rig.order) {
    const node = rig.byId[id]
    lines.push(...link(node, byCarrier.get(id) ?? []))
  }

  for (const id of rig.order) {
    const node = rig.byId[id]
    const parent = node.parent ?? (rootFree ? 'base_link' : null)
    if (!parent) continue
    lines.push(...joints(node, parent, mechs.get(id), off))
  }

  lines.push('</robot>')
  return lines.join('\n') + '\n'
}

/* -------------------------------------------------------------------------
   Glieder
   ------------------------------------------------------------------------- */

function link(node: RigNode, carried: Part[]): string[] {
  const out = [`  <link name="${escape(node.id)}">`]
  if (carried.length === 0) {
    /*
      Ein Glied ohne Bauteil bekommt keine Trägheit und keine Sichtform.

      URDF verlangt beides nicht. Eine erfundene Kugel mit einem Gramm wäre
      bequem und würde in jedem Regler als echte Trägheit mitrechnen.
    */
    out.push('    <!-- kein Bauteil auf diesem Knoten -->')
  }
  for (const part of carried) {
    out.push(...geometry(part))
  }
  out.push('  </link>')
  return out
}

/**
 * Ein Bauteil als Zylinder — Sichtform, Kollisionsform und Trägheit.
 *
 * EIN ZYLINDER UND KEINE KAPSEL: URDF kennt Box, Zylinder, Kugel und Netz.
 * Die Kapsel des Freigangs wird damit an den Enden etwas zu klein. Das ist die
 * falsche Richtung für eine Kollisionsform, deshalb wird der Zylinder um zwei
 * Radien länger gemacht — dann umschliesst er die Kapsel wieder.
 */
function geometry(part: Part): string[] {
  const r = part.radius
  const l = part.length + 2 * r
  const mass = part.mass ?? 0
  /* Vollzylinder um seine Längsachse: die üblichen zwei Formeln. */
  const ixx = mass > 0 ? (mass * (3 * r * r + l * l)) / 12 : 0
  const izz = mass > 0 ? (mass * r * r) / 2 : 0
  const half = part.length / 2

  return [
    `    <!-- ${escape(part.label)} · ${escape(part.provenance)} -->`,
    '    <visual>',
    `      <origin xyz="0 0 ${fixed(-half)}" rpy="0 0 0"/>`,
    `      <geometry><cylinder radius="${fixed(r)}" length="${fixed(l)}"/></geometry>`,
    '    </visual>',
    '    <collision>',
    `      <origin xyz="0 0 ${fixed(-half)}" rpy="0 0 0"/>`,
    `      <geometry><cylinder radius="${fixed(r)}" length="${fixed(l)}"/></geometry>`,
    '    </collision>',
    ...(mass > 0
      ? [
          '    <inertial>',
          `      <origin xyz="0 0 ${fixed(-half)}" rpy="0 0 0"/>`,
          `      <mass value="${fixed(mass)}"/>`,
          `      <inertia ixx="${fixed(ixx)}" ixy="0" ixz="0" iyy="${fixed(ixx)}" iyz="0" izz="${fixed(izz)}"/>`,
          '    </inertial>',
        ]
      : ['    <!-- keine Masse bekannt: geschaetzt, aber nicht zugeordnet -->']),
  ]
}

/* -------------------------------------------------------------------------
   Gelenke
   ------------------------------------------------------------------------- */

/** Die Drehachse in URDF-Schreibweise. */
const AXIS_VECTOR: Record<AxisKey, string> = {
  x: '1 0 0',
  y: '0 1 0',
  z: '0 0 1',
}

const DEG = Math.PI / 180

/**
 * Die Gelenke zwischen einem Knoten und seinem Elternknoten.
 *
 * EIN KNOTEN KANN MEHRERE FREIHEITEN HABEN, URDF-GELENKE HABEN GENAU EINE.
 * Ein Kugelgelenk gibt es dort nicht. Drei Freiheiten werden deshalb zu drei
 * Drehgelenken mit zwei Zwischengliedern ohne Masse — das ist die übliche
 * Auflösung und keine Erfindung. Die Zwischenglieder heissen nach ihrer Achse,
 * damit man sie im Baum wiedererkennt.
 */
function joints(
  node: RigNode,
  parentLink: string,
  mech: ReturnType<typeof mechanisms>[number] | undefined,
  off: ReadonlySet<string>,
): string[] {
  const free = AXIS_KEYS.filter((a) => node.freedom[a] !== null)
  const out: string[] = []

  if (free.length === 0) {
    out.push(
      `  <joint name="${escape(node.id)}_fest" type="fixed">`,
      `    <parent link="${escape(parentLink)}"/>`,
      `    <child link="${escape(node.id)}"/>`,
      `    <origin xyz="${xyz(node)}" rpy="0 0 0"/>`,
      '  </joint>',
    )
    return out
  }

  if (mech && mech.ratio.kind !== 'zahl') {
    out.push(
      `  <!-- ${escape(node.label)}: ${mech.ratio.kind === 'kurve' ? 'Kurbelschwinge' : 'Parallelkinematik'} —`,
      '       geschlossene Kette, in URDF nicht darstellbar. Steht hier als',
      `       Drehgelenk; Nennuebersetzung ${fixed(mech.nominal)} : 1. -->`,
    )
  }

  let parent = parentLink
  free.forEach((axis, i) => {
    const last = i === free.length - 1
    const child = last ? node.id : `${node.id}_${axis}`
    const freedom = node.freedom[axis]!
    const stopped = off.has(`${node.id}.${axis}`) || off.has(node.id)

    if (!last) out.push(`  <link name="${escape(child)}"/>`)
    out.push(
      `  <joint name="${escape(node.id)}_${axis}" type="${stopped ? 'fixed' : 'revolute'}">`,
      `    <parent link="${escape(parent)}"/>`,
      `    <child link="${escape(child)}"/>`,
      /* Der Versatz sitzt am ERSTEN Gelenk der Kette; die weiteren drehen im
         selben Punkt. Sonst wanderte das Gelenk mit jeder Achse weiter. */
      `    <origin xyz="${i === 0 ? xyz(node) : '0 0 0'}" rpy="0 0 0"/>`,
      `    <axis xyz="${AXIS_VECTOR[axis]}"/>`,
      ...(stopped
        ? []
        : [
            `    <limit lower="${fixed(freedom.lo * DEG)}" upper="${fixed(freedom.hi * DEG)}" effort="${fixed(freedom.moment)}" velocity="${fixed(NOMINAL_SPEED)}"/>`,
          ]),
      '  </joint>',
    )
    parent = child
  })

  return out
}

/**
 * Höchstgeschwindigkeit je Achse in rad/s.
 *
 * EINE ANNAHME UND KEINE AUSLEGUNG. URDF verlangt das Feld; der Kern führt
 * keine Achsgrenzgeschwindigkeit, sondern rechnet aus, was die Bewegung
 * verlangt. Ein Wert, der über allem liegt, was der Gang fordert, engt nichts
 * ein und behauptet nichts.
 */
const NOMINAL_SPEED = 20

/* -------------------------------------------------------------------------
   Handwerk
   ------------------------------------------------------------------------- */

/** Millimeter im Modell, Meter in URDF. */
const xyz = (node: RigNode): string =>
  `${fixed(node.at.x / 1000)} ${fixed(node.at.y / 1000)} ${fixed(node.at.z / 1000)}`

/** Sechs Stellen: darunter wandert ein Gelenkpunkt beim Hin- und Herrechnen. */
const fixed = (v: number): string => (Number.isFinite(v) ? v.toFixed(6) : '0.000000')

const escape = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

function groupParts(list: Part[]): Map<string, Part[]> {
  const out = new Map<string, Part[]>()
  for (const part of list) {
    const at = out.get(part.carrier)
    if (at) at.push(part)
    else out.set(part.carrier, [part])
  }
  return out
}
